import React from 'react';
import { MaterialCard } from '@/components/dashboard-material/layout/MaterialDashboardGrid';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, TrendingUp, TrendingDown, Minus, LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import { materialTheme } from '@/lib/material-design-tokens';

// Status types for color-coding
export type IntelligenceCardStatus = 'success' | 'warning' | 'danger' | 'neutral';

// Trend direction for metric changes
export type TrendDirection = 'up' | 'down' | 'neutral';

export interface IntelligenceCardProps {
  // Card identity
  title: string;
  description?: string;
  icon?: LucideIcon;
  
  // Primary metric
  metric: string | number;
  metricLabel?: string;
  metricPrefix?: string; // e.g., "$" for currency
  metricSuffix?: string; // e.g., "%" for percentage
  
  // Context and comparison
  contextText?: string;
  trend?: {
    value: string | number;
    direction: TrendDirection;
    label?: string;
  };
  
  // Status indicator
  status?: IntelligenceCardStatus;
  statusText?: string;
  
  // Quick action
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  
  // States
  isLoading?: boolean;
  error?: string | null;
  
  // Styling
  className?: string;
  contentClassName?: string;
  
  // Responsive
  compact?: boolean;
}

const getStatusStyles = (status: IntelligenceCardStatus) => {
  switch (status) {
    case 'success':
      return {
        iconBg: { backgroundColor: materialTheme.colors.success[50] },
        icon: { color: materialTheme.colors.success[600] },
        text: { color: materialTheme.colors.success[700] },
        badge: {
          backgroundColor: materialTheme.colors.success[50],
          borderColor: materialTheme.colors.success[200],
          color: materialTheme.colors.success[700]
        },
        indicator: { backgroundColor: materialTheme.colors.success[500] },
        metric: { color: materialTheme.colors.success[700] },
      };
    case 'warning':
      return {
        iconBg: { backgroundColor: materialTheme.colors.warning[50] },
        icon: { color: materialTheme.colors.warning[600] },
        text: { color: materialTheme.colors.warning[700] },
        badge: {
          backgroundColor: materialTheme.colors.warning[50],
          borderColor: materialTheme.colors.warning[200],
          color: materialTheme.colors.warning[700]
        },
        indicator: { backgroundColor: materialTheme.colors.warning[500] },
        metric: { color: materialTheme.colors.warning[700] },
      };
    case 'danger':
      return {
        iconBg: { backgroundColor: materialTheme.colors.error[50] },
        icon: { color: materialTheme.colors.error[600] },
        text: { color: materialTheme.colors.error[700] },
        badge: {
          backgroundColor: materialTheme.colors.error[50],
          borderColor: materialTheme.colors.error[200],
          color: materialTheme.colors.error[700]
        },
        indicator: { backgroundColor: materialTheme.colors.error[500] },
        metric: { color: materialTheme.colors.error[700] },
      };
    case 'neutral':
    default:
      return {
        iconBg: { backgroundColor: materialTheme.colors.neutral[50] },
        icon: { color: materialTheme.colors.neutral[600] },
        text: { color: materialTheme.colors.neutral[700] },
        badge: {
          backgroundColor: materialTheme.colors.neutral[50],
          borderColor: materialTheme.colors.neutral[200],
          color: materialTheme.colors.neutral[700]
        },
        indicator: { backgroundColor: materialTheme.colors.neutral[500] },
        metric: { color: materialTheme.colors.neutral[900] },
      };
  }
};

const TrendIcon: React.FC<{ direction: TrendDirection; className?: string }> = ({ direction, className }) => {
  switch (direction) {
    case 'up':
      return <TrendingUp className={cn('w-4 h-4', className)} />;
    case 'down':
      return <TrendingDown className={cn('w-4 h-4', className)} />;
    default:
      return <Minus className={cn('w-4 h-4', className)} />;
  }
};

export const IntelligenceCard: React.FC<IntelligenceCardProps> = ({
  title,
  description,
  icon: Icon,
  metric,
  metricLabel,
  metricPrefix,
  metricSuffix,
  contextText,
  trend,
  status = 'neutral',
  statusText,
  actionLabel,
  onAction,
  actionDisabled = false,
  isLoading = false,
  error,
  className,
  contentClassName,
  compact = false,
}) => {
  const styles = getStatusStyles(status);

  // Loading state
  if (isLoading) {
    return (
      <MaterialCard className={cn('relative h-full flex flex-col', className)}>
        <div className="flex items-center justify-between mb-3">
          {Icon && <Skeleton className="w-8 h-8 rounded-lg" />}
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <div className="mb-4">
          <Skeleton className="h-4 w-32 mb-1" />
          {description && <Skeleton className="h-4 w-40" />}
        </div>
        <div className="mb-4">
          <Skeleton className="h-10 w-12 mb-0.5" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex-1">
          <Skeleton className="h-4 w-full mb-1" />
          <Skeleton className="h-4 w-4/5 mb-4" />
        </div>
        {actionLabel && <Skeleton className="h-8 w-full" />}
      </MaterialCard>
    );
  }

  // Error state
  if (error) {
    return (
      <MaterialCard className={cn('relative', className)}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </MaterialCard>
    );
  }

  return (
    <MaterialCard 
      className={cn('relative h-full flex flex-col', className)}
      hover={true}
      elevation={1}
    >
      {/* Header with Icon and Status Badge */}
      <div className="flex items-center justify-between mb-3">
        {Icon && (
          <div 
            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
            style={styles.iconBg}
          >
            <Icon className="w-4 h-4" style={styles.icon} />
          </div>
        )}
        
        {/* Status Badge */}
        {statusText && (
          <div 
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
            style={{
              gap: '4px',
              ...styles.badge
            }}
          >
            <div 
              className="rounded-full w-1.5 h-1.5"
              style={styles.indicator}
            />
            {statusText}
          </div>
        )}
      </div>

      {/* Title & Description - Tight grouping */}
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-base leading-tight">
          {title}
        </h3>
        {description && (
          <p className="text-gray-600 text-sm leading-tight mt-0.5">
            {description}
          </p>
        )}
      </div>

      {/* Primary Metric - Centered visual unit */}
      <div className="mb-5 text-center">
        <div className="flex flex-col items-center justify-center mb-1">
          <span 
            className="font-bold leading-none text-4xl"
            style={styles.metric}
          >
            {metricPrefix}{metric}{metricSuffix}
          </span>
          {metricLabel && (
            <span className="text-gray-600 text-xs font-medium uppercase tracking-wide mt-1">
              {metricLabel}
            </span>
          )}
          {trend && (
            <div className={cn(
              'flex items-center gap-1 text-sm mt-2',
              trend.direction === 'up' ? 'text-green-600' : 
              trend.direction === 'down' ? 'text-red-600' : 
              'text-gray-600'
            )}>
              <TrendIcon direction={trend.direction} className="w-3 h-3" />
              <span className="font-medium">{trend.value}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Context text - Breathing room */}
      <div className="flex-1">
        {contextText && (
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            {contextText}
          </p>
        )}
      </div>
      
      {/* Action Button - Always at bottom */}
      {actionLabel && onAction && (
        <Button
          onClick={onAction}
          disabled={actionDisabled}
          variant={
            status === 'danger' ? 'destructive' : 
            status === 'warning' ? 'outline' : 
            'secondary'
          }
          size="sm"
          className={cn(
            'w-full text-sm h-8',
            status === 'danger' && 'bg-red-600 hover:bg-red-700 text-white',
            status === 'warning' && 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100',
            status === 'success' && 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
            status === 'neutral' && 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
            actionDisabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {actionLabel}
        </Button>
      )}
    </MaterialCard>
  );
};